import { addExpense, allProjects, getTimesheet } from '../db.js';
import { openDialog } from './dialog.js';
import { mileageRate, orgSetting } from '../org.js';
import { pickerCategories } from '../money-model.js';
import { financeFy27 } from '../fin-pilot.js';
import { ownerFields, sheetKey } from '../me.js';
import { projectPicker, loadYourProjects } from './project-picker.js';
import { mileageBlock, footNote, mileageExpense } from './mileage-block.js';
import { openAddExpense } from './add-expense.js';
import { fmtMiles } from '../expense-maths.js';
import { myMileageAllowed } from '../my-currency.js';
import { INDIA_MILEAGE_MESSAGE } from '../expense-currency.js';
import { toastV4 } from '../ui.js';

// v4.6.0 (build pack 26/10/2026): an EXPENSE now opens Add expense, receipt
// first (add-expense.js). MILEAGE here is the standalone dialog — Expenses ›
// Add mileage and the Calendar's site-visit offer — built from the same block
// Log time carries (mileage-block.js), with the project picker above it. The
// notes below are the history of the older form.
//
// Add Mileage / Add Project Expense (Karan spec 06/07 late, PO flow 07/07):
// - Mileage: miles only — costed at the company's mileage rate, no amount field, no PO.
// - Expense: net amount + VAT + supplier + category (fixed to "Project
//   Expenses" when raised from a timesheet row) + optional receipt upload,
//   optionally linked to a pre-APPROVED purchase order (POs are requested and
//   approved on the Procurement screen — no more auto-PO per expense).
// - Receipts file into Admin → 01. FINANCE → FY → 1.2 CREDITORS → month as
//   ASP-A-1.2_{CATEGORY}_{26June2026}; the Drive write scope is requested at
//   the moment of upload (base connection stays read-only). If part of the
//   path is missing we file as deep as we can and record what was missing.
//
// Migrated onto the canonical dialog shell (openDialog — this is a CREATE, so a
// centred modal per the surface test 7a). Bespoke bits (the mileage site
// picker, the live VAT read-out and the receipt file input) are kept as custom
// DOM via the render() escape hatch, styled with var(--brand-*)/shell tokens; the
// plain fields are rebuilt with field(). Width 520 — most fields are full-width
// or paired, and the receipt UI is a single file input, so 640 isn't warranted.

// The company's mileage rate is settings.mileageRate (org.js mileageRate()); no
// built-in figure. Without one, a mileage claim is refused rather than guessed.
export const EXPENSE_CATEGORIES = [
  'Project Expenses', 'Salaries', 'Software', 'Sub-Contractors', 'Travel',
  'Accountancy', 'Admin', 'Bank', 'Broadband', 'Company Car', 'Dividends',
  'Electricity', 'Human Resources', 'Insurance', 'IT', 'Loan', 'Marketing',
  'Memberships', 'Mobile', 'National Insurance', 'Office Equipment',
  'Office Maintenance', 'Phones', 'Pensions', 'Rates', 'Rent', 'O. Salaries',
  'Site Equipment', 'Training', 'Wellbeing', 'Welfare',
  'Balance Transfer', 'VAT', 'Corporation Tax',
];
// POs and expenses never pick the three ledger-only buckets (the FY 2025–26 import, #235, adds the same export).
export const PICKER_CATEGORIES = EXPENSE_CATEGORIES.filter((c) => !['Balance Transfer', 'VAT', 'Corporation Tax'].includes(c));

/**
 * The list a PO or an expense picks from: everyone gets PICKER_CATEGORIES;
 * the Finance FY 2026–27 pilot also leaves out every category
 * settings.money.categories puts in Not P&L (money-model.js pickerCategories).
 */
export const ledgerPickerCategories = () => (financeFy27() ? pickerCategories(EXPENSE_CATEGORIES, orgSetting('money', {}), { pilot: true }) : PICKER_CATEGORIES);

const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * expenseForm — the one door every screen uses.
 *   type 'Expense' → Add expense, receipt first (add-expense.js).
 *   type 'Mileage' → the standalone Add mileage dialog below.
 * `fixedCategory` is kept for old callers and now only SUGGESTS (Karan 26/10/2026).
 */
export function expenseForm({ session, project = null, type = 'Mileage', fixedCategory = null, suggestedCategory = null, onSaved }) {
  if (type !== 'Mileage') {
    return openAddExpense({ session, project, suggestedCategory: suggestedCategory || fixedCategory, categories: ledgerPickerCategories(), onSaved });
  }
  // Mileage is pounds and miles for everyone, and none for the India office unless
  // settings.money.indiaMileage (Karan 01/11/2026). The screens hide the door; this is the lock.
  return myMileageAllowed(session).then((ok) => {
    if (!ok) { toastV4(INDIA_MILEAGE_MESSAGE); return null; }
    return addMileage({ session, project, onSaved });
  });
}

// The standalone Add mileage: the project picker above the same block Log time
// carries. It keeps its own date (there is no Log time day here); the journey
// text is automatic ("Office → {site} → back").
function addMileage({ session, project, onSaved }) {
  let selected = null;
  let mb = null; let primaryBtn = null; let noteEl = null;
  const f = {};
  const paint = () => {
    if (!mb || !primaryBtn) return;
    const v = mb.view(); const claim = mb.claim();
    const label = claim ? `Add ${fmtMiles(claim.miles)} mi` : 'Add mileage';
    primaryBtn.dataset.verb = label;
    if (!primaryBtn.classList.contains('tf-working')) {
      primaryBtn.textContent = label;
      const ok = !!claim && v.canSave && !!mileageRate();
      primaryBtn.disabled = !ok; primaryBtn.classList.toggle('tf-notyet', !ok);
    }
    if (noteEl && !noteEl.classList.contains('tf-note-err')) {
      if (v.phase === 'working') noteEl.replaceChildren(footNote('Waiting for the route'));
      else noteEl.replaceChildren(document.createTextNode(v.canSave ? '' : 'Enter the miles'));
    }
  };

  const dlg = openDialog({
    title: 'Add mileage',
    subtitle: 'From the office to the site and back, costed for you',
    width: 520,
    render(body, { field, controls }) {
      const pick = document.createElement('div');
      pick.innerHTML = '<div class="tf-labline"><label class="tf-label" for="x-project">Site (project)</label></div>';
      body.appendChild(pick);
      const grid = document.createElement('div');
      grid.className = 'tf-grid'; grid.style.marginTop = '16px';
      f.date = field({ key: 'date', label: 'Date', type: 'date', value: isoToday() });
      controls.set('date', f.date);
      grid.appendChild(f.date.el);
      body.appendChild(grid);
      mb = mileageBlock({
        withTick: false, onChange: paint,
        onAddAddress: (p) => { dlg.close(); document.dispatchEvent(new CustomEvent('track:open-project', { detail: { projectId: p.id, projectNo: p.number } })); },
      });
      mb.el.style.marginTop = '8px';
      body.appendChild(mb.el);

      // The shared searchable picker (v4.6.1). Choosing another site drops the
      // old route at once: a stale route never lands.
      mb.setProject(null);
      const projectsP = allProjects().then((l) => l.filter((p) => p.number && p.status !== 'Void')).catch(() => []);
      const picker = projectPicker({
        id: 'x-project', optional: true, noneLabel: 'No project', placeholder: 'Search project number or client…',
        projects: projectsP,
        suggested: projectsP.then((ps) => loadYourProjects({ session, projects: ps, getTimesheet, sheetKey })).catch(() => []),
        onChange: (id, p) => { selected = p || null; mb.setProject(selected); },
      });
      pick.appendChild(picker.el);
      if (project && project.projectId) {
        projectsP.then((ps) => {
          const p = ps.find((x) => x.id === project.projectId);
          if (p && !selected) { picker.set(p.id); selected = p; mb.setProject(p); }
        });
      }
      setTimeout(() => {
        const card = body.closest('.tf-card');
        primaryBtn = card.querySelector('.tf-btn-primary'); noteEl = card.querySelector('.tf-note');
        paint();
      }, 0);
    },
    primary: {
      label: 'Add mileage',
      onSubmit: async () => {
        if (!mileageRate()) throw new Error('Your company has no mileage rate set yet. Ask the Owner to set one.');
        const v = mb.view(); const claim = mb.claim();
        if (!v.canSave || !claim) throw new Error(v.phase === 'working' ? 'Waiting for the route.' : 'Enter the miles.');
        await addExpense(mileageExpense({ session, ownerFields, date: f.date.get(), project: selected, claim, rate: mileageRate() }));
        mb.dispose();
        if (onSaved) onSaved();
      },
    },
  });
  return dlg;
}
